/**
 * Commission plans (`/commission-plans`, TMMRSL-0049, TMMAPI-0104): the reseller's own plans —
 * what its affiliates earn — as a collection page built as Rate cards is (TMMRSL-0041). Active
 * plans first, then drafts, then closed, each by name; a status filter kept in the URL
 * (`?status=`, replaced, never pushed); a banner and a "Copy to fix" row action for any plan that
 * breaks the agreement's current limits. "New commission plan" creates a SIMPLE draft and opens it.
 * STAFF sees everything, with the actions disabled and explained.
 */
import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Link,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  commissionPlansApi,
  type CommissionPlan,
  type CommissionPlanStatus,
} from '@/api/commissionPlans';
import { useCanWrite } from '@/auth/authHooks';
import { ApiErrorState } from '@/components/ApiErrorState';
import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { describeApiError } from '@/components/useApiErrorHandler';
import { useRemote } from '@/components/useRemote';
import { useToast } from '@/components/useToast';
import { enumParam, useUrlState } from '@/components/useUrlState';
import { ViewOnlyTooltip } from '@/components/ViewOnly';
import { visuallyHidden } from '@/components/visuallyHidden';
import { NoncompliantChip, StatusChip } from '@/components/status/StatusChip';
import { intlLocale } from '@/i18n/constants';
import { useLocale } from '@/i18n/useLocale';
import { Icon } from '@/theme';
import { AdoptCardLink } from './AdoptCardLink';
import { commissionPlanErrorMessage, isNoRateCard } from './commissionPlanErrors';
import { planSummary } from './planSummary';
import { useCopyPlan } from './useCopyPlan';

type Filter = 'all' | 'active' | 'draft' | 'closed';

const FILTER_STATUS: Record<Exclude<Filter, 'all'>, CommissionPlanStatus> = {
  active: 'ACTIVE',
  draft: 'DRAFT',
  closed: 'CLOSED',
};

const STATUS_ORDER: Record<CommissionPlanStatus, number> = { ACTIVE: 0, DRAFT: 1, CLOSED: 2 };

const loadPlans = () => commissionPlansApi.listPlans();

const SCHEMA = { status: enumParam<Filter>(['all', 'active', 'draft', 'closed'], 'all') };

export function CommissionPlansScreen() {
  const { t } = useTranslation();
  const locale = useLocale();
  const location = useLocation();
  const navigate = useNavigate();
  const canWrite = useCanWrite();
  const showToast = useToast();
  const url = useUrlState(SCHEMA);
  const plans = useRemote(loadPlans);
  const { copy, copying } = useCopyPlan();
  const [creating, setCreating] = useState(false);
  const creatingRef = useRef(false);
  const [createError, setCreateError] = useState<{ message: string; noRateCard: boolean } | null>(
    null,
  );

  const filter = url.values.status;
  const all = plans.data ?? [];
  const shown = all
    .filter((plan) => filter === 'all' || plan.status === FILTER_STATUS[filter])
    .sort(
      (a, b) =>
        STATUS_ORDER[a.status] - STATUS_ORDER[b.status] ||
        a.name.localeCompare(b.name, intlLocale(locale)),
    );
  const noncompliant = all.some((plan) => plan.status === 'ACTIVE' && plan.noncompliant);

  function setFilter(next: Filter) {
    url.set({ status: next });
  }

  async function create() {
    if (creatingRef.current) return;
    creatingRef.current = true;
    setCreating(true);
    setCreateError(null);
    try {
      const plan = await commissionPlansApi.createPlan();
      showToast(t('commissionPlans.createdToast'));
      void navigate(`/commission-plans/${plan.id}`);
    } catch (err) {
      setCreateError({
        message: commissionPlanErrorMessage(err, t) ?? describeApiError(err, t).message,
        noRateCard: isNoRateCard(err),
      });
    } finally {
      creatingRef.current = false;
      setCreating(false);
    }
  }

  const newButton = (
    <Button
      variant="contained"
      startIcon={<Icon name="add" />}
      onClick={() => void create()}
      loading={creating}
      disabled={!canWrite}
    >
      {t('commissionPlans.new')}
    </Button>
  );
  const gatedNew = canWrite ? newButton : <ViewOnlyTooltip>{newButton}</ViewOnlyTooltip>;

  function rowAction(plan: CommissionPlan) {
    if (plan.status !== 'ACTIVE' || !plan.noncompliant) return null;
    const button = (
      <Button
        size="small"
        onClick={() => void copy(plan)}
        loading={copying === plan.id}
        disabled={!canWrite || (copying !== null && copying !== plan.id)}
      >
        {t('commissionPlans.action.copyToFix')}
      </Button>
    );
    return canWrite ? button : <ViewOnlyTooltip>{button}</ViewOnlyTooltip>;
  }

  return (
    <Box>
      <PageHeader id="commissionPlans" actions={gatedNew} />
      {createError ? (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          closeText={t('common.close')}
          onClose={() => setCreateError(null)}
          // An `action` replaces the Alert's own close button: NO_RATE_CARD's carries both.
          action={
            createError.noRateCard ? (
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <AdoptCardLink />
                <IconButton
                  size="small"
                  color="inherit"
                  aria-label={t('common.close')}
                  onClick={() => setCreateError(null)}
                >
                  <Icon name="close" size="sm" />
                </IconButton>
              </Stack>
            ) : undefined
          }
        >
          {createError.message}
        </Alert>
      ) : null}

      {plans.status === 'error' ? (
        <ApiErrorState what={t('errors.what.commissionPlans')} error={plans.error} onRetry={plans.reload} />
      ) : null}
      {plans.status === 'loading' && !plans.data ? (
        <Box role="status" aria-label={t('common.loading')}>
          <Skeleton variant="rounded" height={220} />
        </Box>
      ) : null}

      {plans.status !== 'error' && plans.data ? (
        all.length === 0 ? (
          <EmptyState kind="none" title={t('commissionPlans.empty')} action={gatedNew} />
        ) : (
          <Stack spacing={2}>
            {noncompliant ? (
              <Alert severity="warning">{t('commissionPlans.noncompliantBanner')}</Alert>
            ) : null}
            <Box>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={filter}
                aria-label={t('commissionPlans.filter.label')}
                onChange={(_, next: Filter | null) => {
                  if (next !== null) setFilter(next);
                }}
              >
                <ToggleButton value="all">{t('commissionPlans.filter.ALL')}</ToggleButton>
                <ToggleButton value="active">{t('commissionPlans.filter.ACTIVE')}</ToggleButton>
                <ToggleButton value="draft">{t('commissionPlans.filter.DRAFT')}</ToggleButton>
                <ToggleButton value="closed">{t('commissionPlans.filter.CLOSED')}</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {shown.length === 0 ? (
              <EmptyState
                kind="noMatch"
                title={t('commissionPlans.filteredEmpty')}
                action={
                  <Button onClick={() => setFilter('all')}>{t('commissionPlans.showAll')}</Button>
                }
              />
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" aria-label={t('pages.commissionPlans.title')}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('commissionPlans.col.name')}</TableCell>
                      <TableCell>{t('commissionPlans.col.status')}</TableCell>
                      <TableCell>{t('commissionPlans.col.recurring')}</TableCell>
                      <TableCell>{t('commissionPlans.col.appliesTo')}</TableCell>
                      <TableCell align="right">{t('commissionPlans.col.coolOff')}</TableCell>
                      <TableCell align="right">
                        <Box component="span" sx={visuallyHidden}>
                          {t('commissionPlans.col.actions')}
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shown.map((plan) => (
                      <TableRow key={plan.id}>
                        <TableCell component="th" scope="row">
                          <Link
                            component={RouterLink}
                            to={`/commission-plans/${plan.id}`}
                            state={{ listSearch: location.search }}
                          >
                            {plan.name || t('commissionPlans.unnamed')}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <StatusChip domain="rateCard" status={plan.status} />
                          {plan.noncompliant ? <NoncompliantChip /> : null}
                        </TableCell>
                        <TableCell>
                          {planSummary(plan, locale, t) || t('statements.unavailable')}
                        </TableCell>
                        <TableCell>{t(`commissionPlans.appliesTo.${plan.appliesTo}`)}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {t('commissionPlans.coolOffDays', { count: plan.coolOffDays })}
                        </TableCell>
                        <TableCell align="right">{rowAction(plan)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Stack>
        )
      ) : null}
    </Box>
  );
}
